import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigProvider, Flex, Splitter, Typography, Row, Col } from '@app/ui';

import CrashedComponent from '@app/components/errors/CrashedComponent';
import ErrorBoundary from '@app/ErrorBoundary';
import connect from '@app/utils/connect';
import userActions from '@app/actions/user';

import ReloTransparentLogo from './images/LogoTransparent.svg';
import ReloLogo from './images/relo-edge-logo.svg';

import LoginForm from './components/Forms/LoginForm';
import ForgottenPasswordForm from './components/Forms/ForgottenPasswordForm';
import ClientSelectionForm from './components/Forms/ClientSelectionForm';

import './styles/Login.scss';
import { User } from '@app/types/api';
import LanguageSelector from '@app/components/language-selector/LanguageSelector';

type LoginProps = {
    descriptionText: string;
    user: User;
    login: (args: { email: string; password: string }) => void;
    selectClient: (id: User['id']) => void;
    reInitState: () => void;
};

enum LOGIN_SCREENS {
    LOGIN = 'login',
    RESET = 'reset',
    CLIENT_SELECTION = 'client-selection',
}

export const Login = ({
    user,
    descriptionText = '',
    login,
    reInitState,
    selectClient,
}: LoginProps) => {
    const { t } = useTranslation('login');
    const [sizes, setSizes] = useState<(number | string)[]>(['50%', '50%']);
    const [loginScreen, setLoginScreen] = useState<LOGIN_SCREENS>(
        LOGIN_SCREENS.LOGIN,
    );
    const [isPopulatingClients, setPopulatingClients] = useState(false);

    const { isSelectingClient, clients, error } = user;

    const hasError = useMemo(() => {
        return error && typeof error === 'object' && !!error.message;
    }, [error]);

    const hasSsoError = useMemo(() => {
        return error && typeof error === 'object' && error.ssoError;
    }, [error]);

    const initLoginFlow = (values: { email: string; password: string }) => {
        setPopulatingClients(true);
        reInitState();
        const { email, password } = values;
        login({ email, password });
    };

    const backToLoginInitialScreen = () => {
        setLoginScreen(LOGIN_SCREENS.LOGIN);
    };

    useEffect(() => {
        if (hasError || hasSsoError) {
            setPopulatingClients(false);
        } else {
            if (isSelectingClient && clients?.length) {
                setPopulatingClients(false);
                setLoginScreen(LOGIN_SCREENS.CLIENT_SELECTION);
            }

            if (clients.length === 1) {
                selectClient(clients[0].id);
            }
        }
    }, [isSelectingClient, clients, hasError, hasSsoError]);

    return (
        <ErrorBoundary FallbackComponent={CrashedComponent}>
            <ConfigProvider
                theme={{
                    token: {
                        colorPrimary: 'rgba(0, 159, 223, 1)',
                        colorLink: 'rgba(0, 159, 223, 1)',
                        colorError: 'rgba(212, 15, 125, 1)',
                    },
                    components: {
                        Typography: {
                            fontFamily: 'Roboto',
                            fontSize: 16,
                            colorText: 'rgba(0, 0, 0, 0.65)',
                            fontSizeHeading1: 30,
                            colorTextHeading: 'rgba(0, 0, 0, 0.88)',
                        },
                        Input: {
                            colorBorder: 'rgba(0, 0, 0, 0.15)',
                            colorText: 'rgba(0, 0, 0, 0.88)',
                            inputFontSize: 14,
                            colorIcon: 'rgba(0, 0, 0, 0.45)',
                        },
                        Divider: {
                            lineWidth: 1,
                            colorBorder: 'rgba(0, 0, 0, 0.25)',
                        },
                    },
                }}
            >
                <Flex
                    vertical
                    justify="center"
                    align="center"
                    className="-login-container -full-height"
                >
                    <Row gutter={16} style={{ width: '100%' }}>
                        <Col md={24}>
                            <Flex vertical justify="center" align="center">
                                <div id="messages" />
                                <div
                                    className="modal"
                                    id="mainModal"
                                    role="dialog"
                                    aria-labelledby="mainModalLabel"
                                >
                                    <div className="modal-dialog" />
                                </div>
                                <Splitter onResize={setSizes}>
                                    <Splitter.Panel
                                        size={sizes[0]}
                                        resizable={false}
                                    >
                                        <Flex
                                            className="-login-side-panel -full-height"
                                            justify="center"
                                            align="center"
                                        >
                                            <a
                                                href="https://www.relometrics.com"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <img
                                                    className="logo"
                                                    src={ReloTransparentLogo}
                                                />
                                            </a>
                                        </Flex>
                                    </Splitter.Panel>

                                    <Splitter.Panel
                                        size={sizes[1]}
                                        className="-form-container"
                                    >
                                        <Flex
                                            align="center"
                                            vertical
                                            className="-welcome -m-b-4"
                                        >
                                            <a
                                                href="https://www.relometrics.com"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <img
                                                    className="logo"
                                                    src={ReloLogo}
                                                />
                                            </a>
                                            <section className="section section-1 -m-t-2">
                                                <Typography.Title>
                                                    {t('welcomeTitle')}
                                                </Typography.Title>
                                                <Typography.Text>
                                                    {t('welcomeDescription')}
                                                </Typography.Text>
                                            </section>
                                        </Flex>

                                        {descriptionText && (
                                            <Flex className="-description">
                                                <section>
                                                    <Typography.Paragraph>
                                                        {descriptionText}
                                                    </Typography.Paragraph>
                                                </section>
                                            </Flex>
                                        )}

                                        {loginScreen ===
                                            LOGIN_SCREENS.LOGIN && (
                                            <LoginForm
                                                initLoginFlow={initLoginFlow}
                                                initForgotPasswordFlow={() =>
                                                    setLoginScreen(
                                                        LOGIN_SCREENS.RESET,
                                                    )
                                                }
                                                isPopulatingClients={
                                                    isPopulatingClients
                                                }
                                            />
                                        )}

                                        {loginScreen ===
                                            LOGIN_SCREENS.RESET && (
                                            <ForgottenPasswordForm
                                                backToLogin={
                                                    backToLoginInitialScreen
                                                }
                                            />
                                        )}

                                        {loginScreen ===
                                            LOGIN_SCREENS.CLIENT_SELECTION && (
                                            <ClientSelectionForm
                                                backToLogin={
                                                    backToLoginInitialScreen
                                                }
                                            />
                                        )}
                                    </Splitter.Panel>
                                </Splitter>

                                <div className="-language-selector">
                                    <LanguageSelector />
                                </div>
                            </Flex>
                        </Col>
                    </Row>
                </Flex>
            </ConfigProvider>
        </ErrorBoundary>
    );
};

export default connect(['user'], userActions)(Login);
